/**
 * One example row of the queue, with the six columns of the redesigned queue (received,
 * customer, subject, lines, the one action word, who) on invented data. Module 2 and the
 * queue help topic show it. The table form is a one-row DataGrid; the list form of a
 * phone goes through ColumnCards, as the real queue and the practice queue do.
 */

import { Icon } from "@norican/icons";
import { Badge, DataGrid, type DataGridColumnDef } from "@norican/react";

import { ColumnCards } from "@/components/column-cards";
import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { QUEUE_ICON } from "@/lib/line-icons";

import { Figure } from "./figure";

export const QUEUE_ROW_TITLE = "Example row of the queue";

/** The invented row, in the shape of the practice queue (customer note, lines, to decide). */
export interface ExampleQueueRow {
  id: string;
  received: string;
  customer: string;
  customerNote: string;
  subject: string;
  lines: string;
  toDecide: string;
  action: "Confirm";
  who: string;
}

export const EXAMPLE_QUEUE_ROW: ExampleQueueRow = {
  id: "example-queue-1",
  received: "08:12",
  customer: "Sample Steel Works",
  customerNote: "to confirm",
  subject: "Spare parts for the blast machine",
  lines: "1/3 found",
  toDecide: "2 to decide",
  action: "Confirm",
  who: "Nobody",
};

const COLUMNS: DataGridColumnDef<ExampleQueueRow>[] = [
  { id: "received", header: "Received", cell: (row) => row.received },
  {
    id: "customer",
    header: "Customer",
    cell: (row) => (
      <span className="flex min-w-0 flex-col">
        <span className="break-words">{row.customer}</span>
        <span className="text-fg-muted text-xs">{row.customerNote}</span>
      </span>
    ),
  },
  {
    id: "subject",
    header: "Subject",
    cell: (row) => <span className="font-semibold break-words">{row.subject}</span>,
  },
  {
    id: "lines",
    header: "Lines",
    cell: (row) => (
      <span className="flex min-w-0 flex-col">
        <span>{row.lines}</span>
        <span className="text-fg-muted text-xs">{row.toDecide}</span>
      </span>
    ),
  },
  {
    id: "action",
    header: "Action",
    cell: (row) => (
      <Badge tone="accent" variant="subtle" size="sm">
        <Icon icon={QUEUE_ICON[row.action]} size="xs" />
        {row.action}
      </Badge>
    ),
  },
  { id: "who", header: "Who", cell: (row) => row.who },
];

export interface QueueRowFigureProps {
  /** The table (default) or the list form of a phone; the narrow media query picks the list. */
  layout?: "table" | "list";
  caption?: string;
}

export function QueueRowFigure({ layout, caption }: QueueRowFigureProps) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const form = layout ?? (narrow ? "list" : "table");
  return (
    <Figure name="queue-row" title={QUEUE_ROW_TITLE} caption={caption}>
      {form === "list" ? (
        <ColumnCards
          columns={COLUMNS}
          data={[EXAMPLE_QUEUE_ROW]}
          getRowId={(row) => row.id}
          lead={["subject"]}
          label={QUEUE_ROW_TITLE}
        />
      ) : (
        <DataGrid<ExampleQueueRow>
          columns={COLUMNS}
          data={[EXAMPLE_QUEUE_ROW]}
          getRowId={(row) => row.id}
          tableLayout="fixed"
          density="compact"
          aria-label={QUEUE_ROW_TITLE}
          data-layout="table"
        />
      )}
    </Figure>
  );
}
